// Personalized Home; cookbook paths remain in cookbook details.
import React, { useCallback, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useNav } from '../navigation';
import { useHeader, useToday } from '../hooks';
import { StreakButton, StreakSheet, usePersonalStreak } from '../components/HomeHeader';
import { useProgress } from '../store/progress';
import { useSettings } from '../store/settings';
import { IconButton, Row, Screen } from '../components/ui';
import { spacing, usePalette } from '../theme';
import { usePlanner } from '../store/planner';
import * as social from '../platform/social';
import type { CreatorPost, CreatorProfile } from '../types';
import { useRecipeLookup, useLibrary } from '../store/library';
import { useDiet } from '../hooks';
import Photo from '../components/Photo';
import type { MediaRef } from '../types';
import { COUNTRY_COOKBOOKS } from '../../content';

function PinCard({ title, color, onPress, media, size = 150 }: { title: string; color: string; onPress: () => void; media?: MediaRef; size?: number }) {
  const p = usePalette();
  return <Pressable onPress={onPress} style={{ width: '100%', minHeight: size, backgroundColor: color, borderRadius: 18, overflow: 'hidden' }}>
    {media ? <Photo media={media} seed={title} style={{ width: '100%', height: size * 0.68 }} /> : null}
    <Text numberOfLines={2} style={{ color: p.text, fontSize: 16, fontWeight: '700', padding: 14 }}>{title}</Text>
  </Pressable>;
}

export default function HomeScreen() {
  const { t } = useTranslation();
  const nav = useNav();
  const p = usePalette();
  const today = useToday();
  const cookbooks = useLibrary(s => s.cookbooks);
  const follows = useSettings(s => s.followedCreators);
  const [creators, setCreators] = useState<{ creator: CreatorProfile; post?: CreatorPost }[]>([]);
  useFocusEffect(useCallback(() => {
    let active = true;
    social.featuredCreators().then(async suggested => {
      const followed = await Promise.all(follows.map(handle => social.creatorProfile(handle).catch(() => null)));
      const found = [...followed.filter((c): c is CreatorProfile => !!c), ...suggested.filter(c => !follows.includes(c.handle))];
      const items = await Promise.all(found.map(async creator => ({ creator, post: await social.creatorPosts(creator.handle).then(page => page.posts[0], () => undefined) })));
      if (active) setCreators(items);
    }, () => {});
    return () => { active = false; };
  }, [follows]));
  const entries = usePlanner(s => s.entries);
  const lookup = useRecipeLookup();
  const diet = useDiet();
  const planned = [...new Set(entries.filter(e => e.date >= today).sort((a, b) => a.date.localeCompare(b.date)).map(e => e.recipeId))]
    .map(lookup).filter(r => r && !diet.hidden(r));
  const isSuper = useSettings(s => s.isSuper);
  const settle = useProgress(s => s.settle);
  const personal = usePersonalStreak();
  const count = personal.current;
  const lit = personal.todayDone;
  const [streakSheet, setStreakSheet] = useState(false);
  const pins = [
    { key: 'grocery', title: t('library.yourGrocery'), color: p.success + '35', size: 124, onPress: () => nav.navigate('Grocery' as never) },
    { key: 'pantry', title: t('library.yourPantry'), color: p.warning + '35', size: 124, onPress: () => nav.navigate('Pantry' as never) },
    { key: 'planner', title: t('planner.title'), color: p.info + '35', size: 154, onPress: () => nav.navigate('Tabs', { screen: 'PlannerTab' }) },
    { key: 'ai', title: t('home.scan'), color: p.super + '35', size: 124, onPress: () => nav.navigate('Ai' as never) },
    { key: 'library', title: t('library.title'), color: p.info + '25', size: 124, onPress: () => nav.navigate('Library' as never) },
    ...planned.map(r => ({ key: `planned:${r!.id}`, title: r!.title, color: p.card, size: 210, media: r!.image, onPress: () => nav.navigate('Recipe', { id: r!.id }) })),
    ...COUNTRY_COOKBOOKS.map(c => ({ key: `country:${c.id}`, title: `${c.flag} ${c.title}`, color: p.card, size: c.premium ? 230 : 190, onPress: () => nav.navigate('CountryCookbook', { id: c.id }) })),
    ...Object.values(cookbooks).map(c => ({ key: `library:${c.id}`, title: c.title, color: p.card, size: 180 + c.title.length % 70, media: c.image, onPress: () => nav.navigate('Cookbook', { id: c.id }) })),
    ...creators.map(({ creator, post }) => ({ key: `creator:${creator.handle}`, title: post?.caption ? `${creator.displayName}\n${post.caption.slice(0, 90)}` : creator.displayName, color: p.info + '25', size: post?.recipe ? 230 : 160, onPress: () => nav.navigate('Creator', { handle: creator.handle }) })),
  ];
  const columns = [pins.filter((_, index) => index % 2 === 0), pins.filter((_, index) => index % 2 === 1)];

  useFocusEffect(useCallback(() => settle(today, isSuper), [today, isSuper, settle]));

  useHeader(
    {
      title: '',
      headerRight: () => (
        <Row gap={spacing.s}>
          <StreakButton count={count} lit={lit} onPress={() => setStreakSheet(true)} />
          {/* Super is offered only until you have it. */}
          {isSuper ? null : <IconButton icon="super" label={t('super.title')} color={p.super} onPress={() => nav.navigate('Super')} />}
          <IconButton icon="settings" label={t('settings.title')} onPress={() => nav.navigate('Settings')} />
        </Row>
      ),
    },
    [p, t, count, lit, isSuper],
  );

  return (
    <Screen>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.m }}>
        {columns.map((column, i) => <View key={i} style={{ flex: 1, gap: spacing.m }}>{column.map(({ key, ...pin }) => <PinCard key={key} {...pin} />)}</View>)}
      </View>

      <StreakSheet visible={streakSheet} onClose={() => setStreakSheet(false)} />
    </Screen>
  );
}
